import React, { useCallback, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex } from '@atlaskit/primitives/compiled';
import { MenuList } from '@atlaskit/navigation-system';
import {
	STAR,
	UNSTAR,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/star.tsx';
import { FavoriteToggleButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/favorite-toggle-button/index.tsx';
import { MoreNavMenuButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { MEATBALL_DROPDOWN } from '../../constants.tsx';
import { AnalyticContext } from '../../controllers/analytic-context/index.tsx';
import type { DashboardPopupMenu$key } from './__generated__/DashboardPopupMenu.graphql';

type Props = {
	dashboardData: DashboardPopupMenu$key;
};

export const DashboardPopupMenu = ({ dashboardData }: Props): React.JSX.Element => {
	const data = useFragment<DashboardPopupMenu$key>(
		graphql`
			fragment DashboardPopupMenu on JiraDashboard {
				id @required(action: THROW)
				dashboardId @required(action: THROW)
				favouriteValue {
					isFavourite
				}
			}
		`,
		dashboardData,
	);

	const { fireUIAnalyticForFixButton, fireUIAnalyticForFavouriteToggle } =
		useContext(AnalyticContext);

	const handleNavMenuOpen = useCallback(() => {
		fireUIAnalyticForFixButton({ itemId: MEATBALL_DROPDOWN });
	}, [fireUIAnalyticForFixButton]);

	const isFavourite = Boolean(data?.favouriteValue?.isFavourite);

	const handleFavouriteClick = useCallback(() => {
		fireUIAnalyticForFavouriteToggle({
			starType: isFavourite ? UNSTAR : STAR,
			parentItemId: String(data.dashboardId),
		});
	}, [data.dashboardId, fireUIAnalyticForFavouriteToggle, isFavourite]);

	const MenuItems = useCallback(
		() => (
			<MenuList>
				<FavoriteToggleButton
					entityId={data.id}
					defaultIsStarred={isFavourite}
					entityTypeName="JiraDashboard"
					onClick={handleFavouriteClick}
				/>
			</MenuList>
		),
		[data.id, handleFavouriteClick, isFavourite],
	);

	return (
		<Flex gap="space.050" alignItems="center">
			<MoreNavMenuButton onOpen={handleNavMenuOpen} MenuItems={MenuItems} />
		</Flex>
	);
};
